/**
 * RetailerGrowthTrends - Stacked area chart showing revenue contribution by retailer
 * 
 * Shows how each retailer contributes to total revenue over time
 */

import React, { useState } from 'react';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { BarChart2, TrendingUp, Percent } from 'lucide-react';
import { getRetailerInfo } from '../config/retailers';

interface TrendDataPoint {
  fiscal_week: number;
  revenue: number;
  units: number;
  skus: number;
  stores: number;
  by_retailer: Array<{
    retailer_id: string;
    retailer_name: string;
    revenue: number;
    units: number;
    skus: number;
    stores: number;
  }>;
}

interface RetailerGrowthTrendsProps {
  trendData: TrendDataPoint[];
  activeRetailers: Array<{
    retailer_id: string;
    retailer_name: string;
  }>;
}

export function RetailerGrowthTrends({ trendData, activeRetailers }: RetailerGrowthTrendsProps) {
  const [chartType, setChartType] = useState<'grouped' | 'percentage' | 'lines'>('grouped');

  if (!trendData || trendData.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
        <p className="text-gray-500">Not enough historical data to show trends</p>
      </div>
    );
  }

  const formatWeek = (week: number) => {
    const weekNum = week % 100;
    return `W${weekNum}`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: 'compact',
      compactDisplay: 'short'
    }).format(value);
  };

  // Custom tooltip with per-retailer breakdown
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Calculate total for percentage
      const total = payload.reduce((sum: number, entry: any) => sum + (entry.value || 0), 0);
      
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[200px]">
          <p className="text-sm font-semibold text-gray-900 mb-2">{formatWeek(label)}</p>
          <div className="space-y-1">
            {payload.reverse().map((entry: any, index: number) => {
              const percentage = total > 0 ? (entry.value / total) * 100 : 0;
              return (
                <div key={index} className="flex items-center justify-between space-x-3 text-xs">
                  <div className="flex items-center space-x-1.5">
                    <div 
                      className="w-2 h-2 rounded-full" 
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="font-medium text-gray-700">
                      {entry.name}:
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">
                      {formatCurrency(entry.value)}
                    </div>
                    <div className="text-gray-500">
                      {percentage.toFixed(0)}%
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="border-t border-gray-200 mt-2 pt-2 flex items-center justify-between font-semibold">
              <span className="text-gray-700">Total:</span>
              <span className="text-gray-900">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Highly distinct color palette for chart clarity
  const distinctColors = [
    '#3b82f6', // Bright Blue
    '#ef4444', // Bright Red  
    '#10b981', // Bright Green
    '#f59e0b', // Bright Orange
    '#8b5cf6', // Bright Purple
    '#06b6d4', // Bright Cyan
    '#ec4899', // Bright Pink
    '#84cc16', // Lime Green
  ];

  // Get unique retailers from trend data with highly distinct colors
  const uniqueRetailers = React.useMemo(() => {
    const retailerMap = new Map<string, { id: string; name: string; color: string }>();
    let colorIndex = 0;
    
    trendData.forEach(week => {
      week.by_retailer.forEach(retailer => {
        if (!retailerMap.has(retailer.retailer_id)) {
          retailerMap.set(retailer.retailer_id, {
            id: retailer.retailer_id,
            name: retailer.retailer_name,
            color: distinctColors[colorIndex % distinctColors.length]
          });
          colorIndex++;
        }
      });
    });
    
    return Array.from(retailerMap.values());
  }, [trendData]);

  // Transform data for chart - each retailer becomes a column
  const chartData = trendData.map(week => {
    const dataPoint: any = {
      week: week.fiscal_week,
    };
    
    let weekTotal = 0;
    
    // Add each retailer's revenue as a separate column
    if (week.by_retailer && Array.isArray(week.by_retailer)) {
      week.by_retailer.forEach(retailer => {
        if (retailer && retailer.retailer_name && retailer.revenue !== undefined) {
          dataPoint[retailer.retailer_name] = retailer.revenue;
          weekTotal += retailer.revenue;
        }
      });
    }
    
    // Add percentage data for each retailer
    if (week.by_retailer && Array.isArray(week.by_retailer)) {
      week.by_retailer.forEach(retailer => {
        if (retailer && retailer.retailer_name && retailer.revenue !== undefined) {
          const pct = weekTotal > 0 ? (retailer.revenue / weekTotal) * 100 : 0;
          dataPoint[`${retailer.retailer_name}_pct`] = pct;
        }
      });
    }
    
    return dataPoint;
  });

  // Debug log to see data structure
  React.useEffect(() => {
    console.log('[RetailerGrowthTrends] Trend data:', trendData);
    console.log('[RetailerGrowthTrends] Chart data:', chartData);
    console.log('[RetailerGrowthTrends] Unique retailers:', uniqueRetailers);
  }, [trendData, chartData, uniqueRetailers]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      {/* Header with Chart Type Toggle */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Revenue Trends (Last {trendData.length} Weeks)</h3>
          <p className="text-sm text-gray-600">See how each retailer contributes to total revenue over time</p>
        </div>
        
        {/* Chart Type Toggle */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setChartType('grouped')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              chartType === 'grouped'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <BarChart2 className="w-4 h-4 inline-block mr-1" />
            Grouped
          </button>
          <button
            onClick={() => setChartType('percentage')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              chartType === 'percentage'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Percent className="w-4 h-4 inline-block mr-1" />
            % Share
          </button>
          <button
            onClick={() => setChartType('lines')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              chartType === 'lines'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline-block mr-1" />
            Lines
          </button>
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'grouped' ? (
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="week" 
                tickFormatter={formatWeek}
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="rect"
              />
              {uniqueRetailers.map((retailer) => (
                <Bar
                  key={retailer.id}
                  dataKey={retailer.name}
                  fill={retailer.color}
                  name={retailer.name}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          ) : chartType === 'percentage' ? (
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="week" 
                tickFormatter={formatWeek}
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                tickFormatter={(value) => `${value}%`}
                domain={[0, 100]}
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                formatter={(value: number) => `${value.toFixed(1)}%`}
                labelFormatter={(label) => formatWeek(label)}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="rect"
              />
              {uniqueRetailers.map((retailer) => (
                <Bar
                  key={retailer.id}
                  dataKey={`${retailer.name}_pct`}
                  stackId="percentage"
                  fill={retailer.color}
                  name={retailer.name}
                />
              ))}
            </BarChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="week" 
                tickFormatter={formatWeek}
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="line"
              />
              {uniqueRetailers.map((retailer) => (
                <Line
                  key={retailer.id}
                  type="monotone"
                  dataKey={retailer.name}
                  stroke={retailer.color}
                  strokeWidth={3}
                  dot={{ r: 5 }}
                  activeDot={{ r: 7 }}
                  name={retailer.name}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Legend Helper */}
      <div className="mt-4 flex items-center justify-center space-x-4 text-xs text-gray-500">
        <div className="flex items-center space-x-1">
          <BarChart2 className="w-3 h-3" />
          <span>Grouped bars show side-by-side comparison</span>
        </div>
        <span>•</span>
        <div className="flex items-center space-x-1">
          <Percent className="w-3 h-3" />
          <span>% Share shows contribution percentage</span>
        </div>
        <span>•</span>
        <div className="flex items-center space-x-1">
          <TrendingUp className="w-3 h-3" />
          <span>Lines view shows individual trends</span>
        </div>
      </div>
    </div>
  );
}

